// «Ничего не теряется»: если сервер недоступен или сохранение не удалось - копия документа в браузере
// (localStorage этого компьютера) и повтор сохранения с нарастающей паузой; при открытии отчёта - предложение
// восстановить несохранённое. Плашки над листом: конфликт версий (с указанием, кто изменил отчёт - например,
// администратор), отчёт удалён, нет связи, найдена копия (устаревшая - если на сервере более новая ревизия).
import { useSyncExternalStore } from 'react';
import type { RegistryApi } from '../../core/registry';
import { appStore, useApp } from '../../core/store';
import { saveNow } from '../../core/project';
import { api } from '../../core/api';
import { ctx } from '../../core/ctx';
import { askConfirm } from '../../ui/prompts';
import { Btn } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { currentUserId, displayName, whenMe } from '../auth/session';

interface Backup { time: number; revision: number; doc: unknown; meta: unknown; sources: unknown }
// копия - своя у каждого пользователя браузера (на общем компьютере чужой текст не предлагается к восстановлению)
const KEY = (pid: string) => 'sto.backup.' + (currentUserId() ?? 'anon') + '/' + pid;

function readBackup(pid: string): Backup | null {
  try { const s = localStorage.getItem(KEY(pid)); return s ? JSON.parse(s) : null; } catch { return null; }
}
function writeBackup(pid: string) {
  const s = appStore.getState();
  if (!s.editor || s.editor.isDestroyed) return;
  try {
    localStorage.setItem(KEY(pid), JSON.stringify({ time: Date.now(), revision: s.revision, doc: s.editor.getJSON(), meta: s.meta, sources: s.sources }));
  } catch { /* хранилище недоступно или переполнено - остаётся повтор сохранения */ }
}
/** Записать копию открытого отчёта сейчас (потеря сессии и т. п.). */
export function writeBackupNow() {
  const s = appStore.getState();
  if (s.projectId && s.save.state !== 'saved') writeBackup(s.projectId);     // сохранено - копия не нужна
}
function dropBackup(pid: string) { try { localStorage.removeItem(KEY(pid)); } catch { /* нет хранилища */ } }

// --- состояние плашек
type SavedBy = { id: number; login: string; name?: string; role: string } | null | undefined;
/** stale - копия сделана до более новой ревизии на сервере (восстановление заменит чужую/новую правку) */
interface BannerState { restore: Backup | null; stale: { revision: number; by: SavedBy } | null; retryIn: number }
let bs: BannerState = { restore: null, stale: null, retryIn: 0 };
const ls = new Set<() => void>();
const setBs = (p: Partial<BannerState>) => { bs = { ...bs, ...p }; ls.forEach((f) => f()); };
const bstore = { get: () => bs, sub: (f: () => void) => { ls.add(f); return () => { ls.delete(f); }; } };

let retryTimer = 0;
let tick = 0;
let attempt = 0;
let backupTimer = 0;

function scheduleRetry() {
  window.clearTimeout(retryTimer);
  window.clearInterval(tick);
  const delay = Math.min(60, 3 * 2 ** Math.min(attempt, 5));
  attempt++;
  let left = delay;
  setBs({ retryIn: left });
  tick = window.setInterval(() => { left = Math.max(0, left - 1); setBs({ retryIn: left }); }, 1000);
  retryTimer = window.setTimeout(() => { window.clearInterval(tick); void saveNow(true); }, delay * 1000);
}

export function setupBackup(r: RegistryApi) {
  appStore.subscribe((s, p) => {
    const pid = s.projectId;
    if (!pid) return;
    if (s.save.state !== p.save.state) {
      if (s.save.state === 'error' || s.save.state === 'conflict') {
        writeBackup(pid);
        // удалённый отчёт повторять бессмысленно - ждём решения пользователя (плашка)
        if (s.save.state === 'error' && s.save.code !== 'project_deleted') scheduleRetry();
      } else if (s.save.state === 'saved') {
        attempt = 0;
        window.clearTimeout(retryTimer); window.clearInterval(tick);
        setBs({ retryIn: 0 });
        if (!bs.restore) dropBackup(pid);          // предложенную копию не трогаем, пока пользователь не решит
      }
    }
  });
  // подписка закончилась, сервер отказал в сохранении: правки остаются копией в браузере (при продлении их предложат вернуть)
  r.events.on('doc:save-refused', ({ id }: any) => writeBackup(id));
  r.events.on('editor:created', (editor: any) => {
    editor.on('update', () => {
      const st = appStore.getState().save.state;
      if (st === 'error' || st === 'conflict') {
        window.clearTimeout(backupTimer);
        backupTimer = window.setTimeout(() => { const pid = appStore.getState().projectId; if (pid) writeBackup(pid); }, 1500);
      }
    });
  });
  r.events.on('project:opened', ({ id, report }: any) => void whenMe().then(() => {
    if (appStore.getState().projectId !== id) return;
    const b = readBackup(id);
    const same = b && JSON.stringify(b.doc) === JSON.stringify(report.doc);
    const stale = b && typeof b.revision === 'number' && (report.revision || 0) > b.revision
      ? { revision: report.revision, by: report.savedBy as SavedBy } : null;
    if (b && !same) setBs({ restore: b, stale });
    else { if (b) dropBackup(id); setBs({ restore: null, stale: null }); }
  }));
  r.events.on('project:closed', () => setBs({ restore: null, stale: null, retryIn: 0 }));
  // предложенная копия, а документ уже сохранён новее неё - копия устарела (восстановление откатит правки)
  r.events.on('doc:saved', ({ revision }: any) => {
    if (bs.restore && !bs.stale && typeof bs.restore.revision === 'number' && revision > bs.restore.revision) {
      setBs({ stale: { revision, by: null } });
    }
  });
  window.addEventListener('online', () => { if (appStore.getState().save.state === 'error') void saveNow(true); });
}

/** «Кто»: администратор / пользователь / другая вкладка (savedBy - из ответа сервера). */
function whoText(by: SavedBy): string {
  if (!by || by.id === currentUserId()) return '';
  return (by.role === 'admin' ? 'администратор ' : 'пользователь ') + displayName(by as any);
}

/** Сохранить документ (текущий или копию из браузера) отдельным отчётом: копия проекта (с рисунками), если он
 *  ещё есть, иначе новый пустой отчёт того же вида. Открывает новый отчёт. */
async function saveAsNewReport(src?: { doc: unknown; meta: unknown; sources: unknown }) {
  const s = appStore.getState();
  const pid = s.projectId;
  if (!pid) return;
  const data = src || { doc: s.editor?.getJSON(), meta: s.meta, sources: s.sources };
  const base = pid.replace(/^u\d+:/, '');
  const name = `${base} - мои правки`;
  let id: string;
  try { id = (await api.duplicateProject(pid, name)).id; } catch {
    id = (await api.createProject({ name, type: (data.meta as any)?.type || 'lab', template: 'blank' })).id;
  }
  await api.saveProject(id, { doc: data.doc as any, meta: data.meta as any, sources: data.sources as any, force: true });
  dropBackup(pid);
  setBs({ restore: null, stale: null });
  s.set({ save: { state: 'saved' } });
  ctx.toast(`Ваши правки сохранены отдельным отчётом «${id.replace(/^u\d+:/, '')}»`, 'ok', 6000);
  location.hash = '#/p/' + encodeURIComponent(id);
}

async function overwriteConflict() {
  const info = appStore.getState().save.info;
  const who = whoText(info?.savedBy);
  const ok = await askConfirm({ title: 'Сохранить вашу версию поверх?', danger: true, ok: 'Сохранить поверх', testid: 'overwrite-confirm',
    text: who ? `Отчёт изменил ${who}. Его правка будет заменена вашей версией (она останется в «Файл → Версии»).`
      : 'Отчёт изменён в другой вкладке или окне. Та версия будет заменена вашей (она останется в «Файл → Версии»).' });
  if (!ok) return;
  await saveNow(true, true);
}

async function restoreBackup() {
  const b = bs.restore;
  const s = appStore.getState();
  if (!b || !s.editor) return;
  if (bs.stale) {
    const who = whoText(bs.stale.by);
    const ok = await askConfirm({ title: 'Восстановить устаревшую копию?', danger: true, ok: 'Восстановить поверх', testid: 'restore-stale-confirm',
      text: `Копия сделана до более новой версии на сервере${who ? ` (изменил ${who})` : ''}. Текущий текст отчёта будет заменён копией - ` +
        'новые правки останутся только в «Файл → Версии». Надёжнее - «Сохранить копию отдельным отчётом».' });
    if (!ok) return;
  }
  s.editor.commands.setContent(b.doc as any, { emitUpdate: true });
  s.set({ meta: b.meta as any, sources: b.sources as any });
  s.markDirty();
  setBs({ restore: null, stale: null });
  await saveNow(true, true);
  ctx.toast('Несохранённые изменения восстановлены', 'ok');
}

const hhmm = (s?: string) => { const d = new Date(s || ''); return isNaN(+d) ? '' : d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }); };

/** Плашки над листом: конфликт, отчёт удалён, нет связи, найдена копия. */
export function Banners() {
  const save = useApp((s) => s.save);
  const b = useSyncExternalStore(bstore.sub, bstore.get);
  const out: React.ReactNode[] = [];
  if (b.restore) {
    const drop = () => { const pid = appStore.getState().projectId; if (pid) dropBackup(pid); setBs({ restore: null, stale: null }); };
    const r = b.restore;
    if (b.stale) {
      const who = whoText(b.stale.by);
      out.push(
        <div key="restore" className="banner banner-warn" data-testid="banner-restore" data-stale="1">
          <Icon name="restore" size={16} />
          <span>Найдена копия ваших правок от {new Date(r.time).toLocaleString('ru-RU')} (в браузере), но она <b>устарела</b>: на сервере
            более новая версия{who ? `, её сохранил ${who}` : ''}. Восстановление заменит текущий текст.</span>
          <Btn label="Сохранить копию отдельным отчётом" primary onClick={() => void saveAsNewReport(r).catch((e) => ctx.toast(e.message, 'error'))} testid="restore-as-new" />
          <Btn label="Восстановить поверх" onClick={restoreBackup} testid="restore-apply" />
          <Btn label="Удалить копию" onClick={drop} testid="restore-drop" />
        </div>);
    } else {
      out.push(
        <div key="restore" className="banner banner-info" data-testid="banner-restore">
          <Icon name="restore" size={16} />
          <span>Найдены несохранённые изменения от {new Date(r.time).toLocaleString('ru-RU')} (копия в браузере).</span>
          <Btn label="Восстановить" primary onClick={restoreBackup} testid="restore-apply" />
          <Btn label="Удалить копию" onClick={drop} testid="restore-drop" />
        </div>);
    }
  }
  if (save.state === 'conflict') {
    const who = whoText(save.info?.savedBy);
    out.push(
      <div key="conflict" className="banner banner-warn" data-testid="banner-conflict">
        <Icon name="warn" size={16} />
        {who
          ? <span data-testid="conflict-who">Отчёт изменил <b>{who}</b>{save.info?.modified ? ` в ${hhmm(save.info.modified)}` : ''}. Ваши правки не потеряны - копия в браузере.</span>
          : <span>Этот отчёт изменён в другой вкладке или окне. Копия ваших правок сохранена в браузере.</span>}
        <Btn label={who ? 'Загрузить его версию' : 'Загрузить их версию'} onClick={() => location.reload()} testid="conflict-reload" />
        <Btn label="Сохранить мои правки отдельным отчётом" onClick={() => void saveAsNewReport().catch((e) => ctx.toast(e.message, 'error'))} testid="conflict-as-new" />
        <Btn label="Сохранить мою поверх" primary={!who} onClick={() => void overwriteConflict()} testid="conflict-overwrite" />
      </div>);
  } else if (save.state === 'error' && save.code === 'project_deleted') {
    const byAdmin = !!save.info?.byAdmin;
    out.push(
      <div key="deleted" className="banner banner-error" data-testid="banner-deleted">
        <Icon name="trash" size={16} />
        <span>Этот отчёт удалён{byAdmin ? <b> администратором</b> : ' (он в корзине)'}. Ваши правки не потеряны - копия в браузере
          {byAdmin ? '; если администратор восстановит отчёт, при открытии будет предложено их вернуть.' : '; после восстановления из корзины их можно вернуть.'}</span>
        <Btn label="Сохранить правки отдельным отчётом" primary onClick={() => void saveAsNewReport().catch((e) => ctx.toast(e.message, 'error'))} testid="deleted-as-new" />
        <Btn label="К списку отчётов" onClick={() => { location.hash = '#/'; }} testid="deleted-home" />
      </div>);
  } else if (save.state === 'error') {
    out.push(
      <div key="error" className="banner banner-error" data-testid="banner-offline">
        <Icon name="offline" size={16} />
        <span>Не удаётся сохранить: {save.error || 'нет связи с редактором'}. Правки не потеряются - копия в браузере{b.retryIn ? `, повтор через ${b.retryIn} с` : ''}.</span>
        <Btn label="Повторить сейчас" onClick={() => { attempt = 0; void saveNow(true); }} />
      </div>);
  }
  return out.length ? <div className="banners">{out}</div> : null;
}
